/**
 * Formatage des valeurs de métriques selon le `format` déclaré dans le registry
 * backend (`src/engine/registry/*.json`) : number | percent | currency | duration.
 */

export type MetricFormat = 'number' | 'percent' | 'currency' | 'duration';

/** Formate une durée en secondes → `1h 04m`, `4m 12s`, `38s`. */
function formatDuration(seconds: number): string {
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

export interface FormatOptions {
  /** Décimales forcées (sinon : 0 pour un entier, 2 pour un décimal). */
  decimals?: number;
  prefix?: string;
  suffix?: string;
}

/** Rend une valeur numérique lisible selon le format de la métrique (locale FR). */
export function formatMetricValue(
  value: number | string | null | undefined,
  format: MetricFormat = 'number',
  options: FormatOptions = {},
): string {
  const num = typeof value === 'number' ? value : Number(value);
  if (value === null || value === undefined || value === '' || Number.isNaN(num)) return '—';

  const { decimals, prefix = '', suffix = '' } = options;

  let body: string;
  switch (format) {
    case 'duration':
      body = formatDuration(num);
      break;
    case 'percent': {
      // `computeRatios` (backend) renvoie les ratios en base 1 (num/den) : 0.42 → 42,0 %.
      const pct = num * 100;
      body = `${pct.toLocaleString('fr-FR', {
        minimumFractionDigits: decimals ?? 1,
        maximumFractionDigits: decimals ?? 1,
      })} %`;
      break;
    }
    case 'currency':
      body = num.toLocaleString('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: decimals ?? 2,
        maximumFractionDigits: decimals ?? 2,
      });
      break;
    default: {
      const isInt = Number.isInteger(num);
      body = num.toLocaleString('fr-FR', {
        minimumFractionDigits: decimals ?? (isInt ? 0 : 2),
        maximumFractionDigits: decimals ?? (isInt ? 0 : 2),
      });
    }
  }

  return `${prefix}${body}${suffix}`;
}
